import logo from '../../assets/logos/sparkMotionLogo.png';
import backgroundVideo from '../../assets/Video/Bg-video.mp4'; // Ensure you add the video file in your assets folder
import React, { useState, useEffect } from 'react';
import { useDispatch } from "react-redux";
import { apiConnecter } from '../../services/apiConnector';
import Modal from '../../components/shared/ErrorModal';
import { login } from '../../redux/slices/userInfoSlice';
import { useNavigate } from 'react-router';
import API_ENDPOINTS from '../../constant/EndPoints';
import { IoMdEyeOff, IoMdEye } from "react-icons/io";

/**
 * Renders the SparkMotion application logo.
 * @returns {JSX.Element} The image element for the logo.
 */
const SparkMotionLogo = () => (
    <img src={logo} className='mb-3 w-[160px] h-[50px] object-contain' alt="SparkMotion Logo" />
);

/**
 * The main component for the user login page.
 * It handles form submission, input validation, API communication for authentication,
 * and state management for loading, errors, and success feedback.
 *
 * @returns {JSX.Element} The Login page UI.
 */
const Login = () => {
    /* ****************************************************************
    *                       ROUTER/REDUX HOOKS                        *
    ******************************************************************/
    const navigate = useNavigate();
    const dispatch = useDispatch();

    /* ****************************************************************
    *                            STATE                                *
    ******************************************************************/
    const [email, setEmail] = useState(''); // State for the email input value
    const [password, setPassword] = useState(''); // State for the password input value
    const [loading, setLoading] = useState(false); // Controls the loading state during API call
    const [error, setError] = useState(''); // Stores API error messages
    const [success, setSuccess] = useState(''); // Stores success messages
    const [showPassword, setShowPassword] = useState(false); // Toggles password visibility
    const [isLoggedIn, setisLoggedIn] = useState(false); // Tracks successful login status (for redirect delay)
    const [fieldErrors, setFieldErrors] = useState({ email: '', password: '' }); // Stores client-side validation errors

    /* ****************************************************************
    *                          HANDLERS                               *
    ******************************************************************/

    /**
     * Toggles the visibility state of the password input field.
     */
    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    /**
     * Handles the form submission for user login.
     * Performs client-side validation before making the API call.
     * @param {React.FormEvent<HTMLFormElement>} e - The form submission event.
     */
    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        // 1. Client-Side Validation
        let isValid = true;
        const newFieldErrors = { email: '', password: '' };

        if (!email.trim()) {
            newFieldErrors.email = 'Email is required.';
            isValid = false;
        } else if (!/\S+@\S+\.\S+/.test(email)) {
            newFieldErrors.email = 'Please enter a valid email address.';
            isValid = false;
        }

        if (!password) {
            newFieldErrors.password = 'Password is required.';
            isValid = false;
        }

        setFieldErrors(newFieldErrors);

        if (!isValid) return; // Stop if validation failed

        // 2. API Call
        setLoading(true);
        try {
            const response = await apiConnecter("POST", API_ENDPOINTS.REACT_APP_LOGIN_END_POINT, { email, password });
            
            setSuccess("Redirecting To Dashboard...");
            setisLoggedIn(true); // Prevent re-submission while redirecting
            
            // Dispatch login action and navigate after a brief success delay
            setTimeout(() => {
                dispatch(login({ user: response?.data?.result?.user, token: response?.data?.result?.token }));
                navigate("/Overview");
            }, 2000);

        } catch (err) {
            // Handle API error and display message
            setError(err?.response?.data?.message ?? err.message);
        } finally {
            // Reset loading state
            setLoading(false);
        }
    };

    /* ****************************************************************
    *                     HELPER FUNCTIONS                            *
    ******************************************************************/

    /**
     * Dynamically determines the Tailwind CSS class for input border styling
     * based on whether a client-side validation error exists for the field.
     * @param {string} fieldName - The name of the field ('email' or 'password').
     * @returns {string} The appropriate border class string.
     */
    const getInputBorderClass = (fieldName) => {
        return fieldErrors[fieldName]
            ? "border-orange-600 focus:border-orange-600"
            : "border-transparent focus:border-[var(--color-primary)]";
    };

    /* ****************************************************************
    *                         EFFECT HOOKS                           *
    ******************************************************************/

    /**
     * Effect to trigger the Modal component whenever 'error' or 'success' state changes.
     */
    useEffect(() => {
        if (error) {
            // Display error modal when an error occurs
            Modal({ title: "Login Failed", message: error });
        }
        if (success) {
            // Display success modal upon successful API response
            Modal({ title: "Login Successful", message: success });
        }
    }, [error, success]);

    /* ****************************************************************
    *                             RENDER                              *
    ******************************************************************/

    return (
        <>
            <div
                className="min-h-screen w-full font-[Inter] flex justify-center items-center p-4 relative"
                style={{
                    backgroundColor: 'var(--color-surface-background)',
                }}
            >
                {/* Video background - Full screen, muted, and looping */}
                <video
                    autoPlay
                    loop
                    muted
                    className="absolute top-0 left-0 w-full h-full object-cover"
                    src={backgroundVideo}
                    type="video/mp4"
                ></video>

                {/* Black overlay for better form readability */}
                <div className="absolute top-0 left-0 w-full h-full bg-black opacity-60 z-0"></div>

                <div className='flex flex-col justify-center items-center w-full max-w-md relative z-10'>
                    <SparkMotionLogo />
                    <div className="flex flex-col bg-[var(--color-surface-card)] rounded-[12px] border-[#454343] border pb-6 shadow-2xl w-full px-6 sm:px-8">

                        {/* Header Section */}
                        <div className="text-center mt-5 ">
                            <h2 className="text-[20px] tracking-tight text-[var(--color-text-base)]">
                                Welcome back
                            </h2>
                            <p className="mt-1 text-[13px] text-[var(--color-text-base)] leading-5">
                                Sign in to your SparkMotion dashboard
                            </p>
                        </div>

                        {/* Login Form */}
                        <form onSubmit={handleLogin} className="mt-2 space-y-4">

                            {/* Email Input Field */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-[var(--primary-text-color)]">
                                    Email Address
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="email"
                                        type="text"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="Enter Your Email"
                                        className={`block w-full rounded-md bg-[var(--color-surface-input)] px-3 py-2 text-sm text-[var(--primary-text-color)] placeholder-[var(--color-input-placeholder)] outline-none border transition-colors duration-200 ${getInputBorderClass('email')}`}
                                    />
                                    {/* Display email validation error */}
                                    {fieldErrors.email && (
                                        <p className="mt-1 text-xs text-orange-600">{fieldErrors.email}</p>
                                    )}
                                </div>
                            </div>

                            {/* Password Input Field with Toggle */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-bold text-[var(--color-text-base)]">
                                    Password
                                </label>
                                <div className="mt-2 relative">
                                    <input
                                        id="password"
                                        type={showPassword ? "text" : "password"} // Dynamic type based on state
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        placeholder="Enter Password"
                                        className={`block w-full rounded-md bg-[var(--color-surface-input)] px-[12px] py-[6px] text-sm text-[var(--color-text-base)] placeholder-[var(--color-input-placeholder)] outline-none border transition-colors duration-200 ${getInputBorderClass('password')}`}
                                    />
                                    {/* Password visibility toggle button */}
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-300 transition-colors duration-200 text-lg"
                                    >
                                        {showPassword ? <IoMdEyeOff /> : <IoMdEye />}
                                    </button>
                                </div>
                                {/* Display password validation error */}
                                {fieldErrors.password && <p className="mt-1 text-xs text-orange-600">{fieldErrors.password}</p>}
                            </div>

                            {/* Submit Button */}
                            <div>
                                <button
                                    type="submit"
                                    disabled={loading || isLoggedIn} // Disable while loading or already logged in
                                    className={`flex w-full justify-center rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-semibold text-white shadow-sm transition-opacity cursor-pointer duration-200 ${loading || isLoggedIn ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'hover:opacity-80'
                                        }`}
                                >
                                    {loading ? 'Signing in...' : 'Sign in'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Login;
